import PageService from "utilities/services/notebooks/page-service";
import useNotebooksAtom from "utilities/hooks/domain/notebooks/use-notebooks-atom";
import {
    currentPageAtom,
    currentPageLoadingAtom,
} from "utilities/atoms/notebooks-atom";
import { useAtom } from "jotai";
import { useCallback } from "react";
import Page from "models/interfaces/page";
import useMostRecentlyEditedOn from "utilities/hooks/domain/notebooks/pages/use-most-recently-edited-on";
import { useQueryClient } from "react-query";

const ONE_MINUTE_IN_MS = 60 * 1000;

const useCurrentPage = () => {
    const [currentPage, setCurrentPageAtom] = useAtom(currentPageAtom);
    const { renamePage, replacePage, updatePage } = useNotebooksAtom();
    const { get: getPageAPi } = PageService.useGet();
    const [loading, setLoading] = useAtom(currentPageLoadingAtom);
    const { refreshMostRecentlyEditedOn } = useMostRecentlyEditedOn(
        currentPage?.id
    );

    const clear = () => setCurrentPageAtom(null);

    // Intentionally leaving this here as reference.
    // The queryClient uses the same query key as PageService.useGetQuery, so this will update the cached result for other hooks using the same query.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ = PageService.useGetQuery;
    const queryClient = useQueryClient();

    const refresh = useCallback(
        async (pageId: number | null) => {
            if (loading) {
                return;
            }

            try {
                setLoading(true);

                if (pageId == null || isNaN(pageId)) {
                    throw Error;
                }

                const routeParams = { id: pageId };
                const queryParams = undefined;
                const queryKey = [
                    PageService.getPageKey,
                    { routeParams, queryParams },
                ];

                const result = await queryClient.fetchQuery(
                    queryKey,
                    () => getPageAPi(routeParams),
                    { staleTime: ONE_MINUTE_IN_MS }
                );

                // const result = await getPageAPi({ id: pageId });

                if (result.result?.hasErrors() || result.resultObject == null) {
                    throw Error;
                }

                const page = result.resultObject;
                setCurrentPageAtom(page);
                replacePage(page);
            } catch (error) {
                setCurrentPageAtom(null);
            } finally {
                setLoading(false);
            }
        },
        [
            getPageAPi,
            loading,
            queryClient,
            replacePage,
            setCurrentPageAtom,
            setLoading,
        ]
    );

    const rename = async (newName: string) => {
        if (currentPage == null || currentPage.id == null) {
            return;
        }

        setCurrentPageAtom(currentPage.with({ name: newName }));
        renamePage(currentPage.id!, newName);
        await refreshMostRecentlyEditedOn();
    };

    const updateCustomLogo = async (
        newFileId: number | undefined,
        newAltText: string
    ) => {
        if (currentPage == null || currentPage.id == null) {
            return;
        }

        const updatedCustomLogoMeta: Partial<Page> = {
            customLogoFileId: newFileId,
            customLogoAltText: newAltText,
        };

        setCurrentPageAtom(currentPage.with(updatedCustomLogoMeta));

        updatePage(currentPage.with(updatedCustomLogoMeta));
        await refreshMostRecentlyEditedOn();
    };

    return {
        clear,
        currentPage,
        loading,
        refresh,
        rename,
        updateCustomLogo,
    };
};

export default useCurrentPage;
